import { forwardRef, useMemo } from 'react'
import { EChart, type EChartHandle } from './EChart'
import { buildTimeSeriesOption, type TimeSeriesInput } from './timeSeriesOption'
import { useChartTheme } from './useChartTheme'

export interface TimeSeriesChartProps extends TimeSeriesInput {
  height?: number
  loading?: boolean
  ariaLabel?: string
  className?: string
  texture?: boolean
  /** Клик по дню/неделе (в любом месте области по этой дате) — обычно открывает панель детализации. */
  onPointClick?: (index: number, date: string, seriesId?: string) => void
  /** Выделение диапазона мышью — обычно меняет период фильтра. */
  onRangeSelect?: (from: string, to: string) => void
}

/**
 * Ряд по дням/неделям: линия, область или столбцы, ряд сравнения пунктиром, отметки событий, ползунок масштаба,
 * перекрестие с подсказкой по всем рядам. Клик по дате и выделение диапазона — через колбэки.
 */
export const TimeSeriesChart = forwardRef<EChartHandle, TimeSeriesChartProps>(function TimeSeriesChart(
  { height = 320, loading, ariaLabel, className, texture, onPointClick, onRangeSelect, ...input },
  ref,
) {
  const theme = useChartTheme()
  const option = useMemo(
    () => buildTimeSeriesOption(input, theme),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      theme,
      input.dates,
      input.series,
      input.kind,
      input.events,
      input.valueFormat,
      input.axisFormat,
      input.dateFormat,
      input.tooltipDateFormat,
      input.zoom,
      input.fromZero,
      input.eventTypeLabels,
      input.bandEventTypes,
    ],
  )

  return (
    <EChart
      ref={ref}
      option={option}
      height={height}
      loading={loading}
      ariaLabel={ariaLabel}
      className={className}
      texture={texture}
      onAxisClick={
        onPointClick
          ? (index, seriesId) => {
              const date = input.dates[index]
              if (date !== undefined) onPointClick(index, date, seriesId)
            }
          : undefined
      }
      onBrushX={
        onRangeSelect
          ? (start, end) => {
              const from = input.dates[start]
              const to = input.dates[end]
              if (from !== undefined && to !== undefined && start !== end) onRangeSelect(from, to)
            }
          : undefined
      }
    />
  )
})

export default TimeSeriesChart
